import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Loader2, Mail, Phone, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([{role:'ai',text:'Czesc! Jestem asystentem Torr.pl. W czym moge pomoc?'}]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const chatEnd = useRef(null);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const r = await api.post('/chat/message', { message: msg, session_id: sessionId });
      setChatMessages(prev => [...prev, { role: 'ai', text: r.data.response }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Przepraszam, wystapil blad. Napisz do nas: kontakt@torr.pl' }]);
    } finally { setChatLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Kontakt</h1>
        <p className="text-muted-foreground mt-2">Masz pytania? Skontaktuj sie z nami lub skorzystaj z czatu AI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Dane kontaktowe</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-primary" /><div><p className="text-sm font-medium">Email</p><p className="text-xs text-muted-foreground">kontakt@torr.pl</p></div></div>
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">Zgloszenia naduzyc</p><p className="text-xs text-muted-foreground">abuse@torr.pl</p></div></div>
              <Separator />
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-[#1877F2] fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <div><p className="text-sm font-medium">Facebook</p><a href="https://facebook.com/torrpl" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">fb.com/torrpl</a></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wspolpraca</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">Zapraszamy do wspolpracy w ramach programu afiliacyjnego. Jesli posiadasz baze adresow email lub numerow telefonow, mozesz zmonetyzowac swoje zasoby z podzialem zysku 50/50. Nie ponosisz zadnych kosztow wysylki.</p>
              <p className="text-sm text-muted-foreground mt-3">Napisz do nas lub zarejestruj sie w panelu.</p>
            </CardContent>
          </Card>
        </div>

        {/* AI Chat */}
        <Card className="border-border/50 bg-card/80 flex flex-col" style={{ minHeight: 480 }}>
          <CardHeader className="pb-2 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><MessageCircle className="h-4 w-4 text-primary" /></div>
              <div><CardTitle className="font-heading text-base">Czat z asystentem AI</CardTitle><p className="text-xs text-muted-foreground">Odpowiada natychmiast 24/7</p></div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0">
            <ScrollArea className="flex-1 px-4">
              <div className="space-y-3 py-4">
                {chatMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {chatLoading && <div className="flex justify-start"><div className="bg-muted px-3 py-2 rounded-lg"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div></div>}
                <div ref={chatEnd} />
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border/50">
              <div className="flex gap-2">
                <Input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Napisz wiadomosc..." className="bg-background/50"
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()} data-testid="chat-input" />
                <Button size="icon" onClick={sendChat} disabled={chatLoading || !chatInput.trim()} data-testid="chat-send">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
